import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import "../screens/Home.css";
import "../screens/StudentHome.css";
import Footer from "../components/Footer";
import { useAuth0 } from "@auth0/auth0-react";
import { usePermissions } from "../context/PermissionContext";
import UserDetailsModal from "../components/UserDetailsModal";
import MainImageHeader from "../components/MainImageHeader";
import MainButtonControls from "../components/MainButtonControls";
import HomeCarousel from "../components/HomeCarousel";
import MainOfferings from "../components/MainOfferings";
import MainAbout from "../components/MainAbout";
import MainContact from "../components/MainContact";
import AnimateComponent from "../components/AnimateComponent";
import PendingFeePopup from "../components/PendingFeePopup";
import FinancialAidModal from "../components/FinancialAidModal";
import { filterAdminNavOptions } from "../utils/adminNavigation";
import { emailsEqual } from "../utils/emailCompare";
import { BRAND } from "../utils/brand";
import AdminDashboard from "../components/AdminDashboard";
import * as usersApi from "../api/users";
import * as registrationsApi from "../api/registrations";
import * as publicApi from "../api/publicApi";
import * as financialAidApi from "../api/financialAid";
import useStudentLandingScroll from "../hooks/useStudentLandingScroll";
import { resolveStudentFeeState } from "../utils/studentFeeState";

export default function Home() {
  const { user, isAuthenticated, logout } = useAuth0();
  const { permissions, loading: permsLoading } = usePermissions();
  const [email, setEmail] = useState("");
  const [showCheckout, setShowCheckout] = useState(false);
  const [userId, setUserId] = useState("");

  // Normal user state
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [regObj, setRegObj] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [showFinancialAidModal, setShowFinancialAidModal] = useState(false);

  //  Extract email after login
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      setEmail(user.email);
    }
  }, [isAuthenticated, user]);

  const isSuperAdmin = emailsEqual(
    email,
    process.env.REACT_APP_SUPER_USER_EMAIL,
  );
  const adminName = user?.name || (isSuperAdmin ? "Sir Zain" : "User");

  const visibleAdminOptions = useMemo(() => {
    if (!email || permsLoading) return [];
    return filterAdminNavOptions(permissions, { isSuperAdmin });
  }, [email, permissions, permsLoading, isSuperAdmin]);

  const isAdmin = visibleAdminOptions.length > 0;
  const awaitingPermissions = Boolean(isAuthenticated && permsLoading);
  const hasMyCourses =
    isSuperAdmin ||
    (Array.isArray(permissions) && permissions.includes("view_my_courses"));

  //  Check if first login (only for normal users)
  useEffect(() => {
    const checkUser = async () => {
      if (awaitingPermissions || isAdmin || !email) return;

      try {
        const response = await usersApi.checkUser();
        if (response.data.isFirstLogin) {
          setIsFirstLogin(true);
        } else {
          const userRes = await usersApi.lookup({ email });
          setUserId(userRes.data.id);
        }
      } catch (error) {
        console.error("Error checking user:", error);
      }
    };

    checkUser();
  }, [email, isAdmin, awaitingPermissions]);

  // Pending-fee popup: only for users who can access My Courses
  useEffect(() => {
    if (!user || awaitingPermissions || !hasMyCourses) {
      if (!hasMyCourses) {
        setRegObj(null);
        setShowCheckout(false);
      }
      return;
    }

    let cancelled = false;
    async function fetchData() {
      try {
        const myCoursesRes = await registrationsApi.myCourses();
        if (!cancelled) {
          setRegObj(myCoursesRes.data?.[0] ?? null);
        }
      } catch (error) {
        if (!cancelled) {
          setRegObj(null);
          setShowCheckout(false);
        }
        console.error("Error loading my courses on Home:", error);
      }
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [user, awaitingPermissions, hasMyCourses]);

  //  Teachers list
  useEffect(() => {
    async function fetchData() {
      const response = await publicApi.getTeachers();
      setTeachers(response.data);
    }
    fetchData();
  }, []);

  // Fee popup — same rule as My Courses (pending enrollments, else ledger)
  useEffect(() => {
    if (!regObj) {
      setShowCheckout(false);
      return;
    }
    setShowCheckout(resolveStudentFeeState(regObj).needsPayment);
  }, [regObj]);

  //  Financial Aid check
  useEffect(() => {
    async function checkFinancialAidRecord() {
      try {
        const { data } = await financialAidApi.checkRecord({ email });
        if (!data.valid) setShowFinancialAidModal(true);
      } catch (error) {
        console.error("Error checking financial aid record:", error);
      }
    }
    if (
      isAuthenticated &&
      email &&
      regObj &&
      regObj.courses.length > 0 &&
      regObj.feemode === "partial"
    ) {
      setUserId(regObj.userId);
      checkFinancialAidRecord();
    }
  }, [isAuthenticated, email, regObj]);

  const handleDetailsSaved = () => setIsFirstLogin(false);
  const handleFinancialDetailsSaved = () => setShowFinancialAidModal(false);

  const showStudentLanding = !awaitingPermissions && !isAdmin;
  const { scrolled: navScrolled, enterContent } = useStudentLandingScroll(
    showStudentLanding && !isFirstLogin,
  );

  return (
    <div className={isAdmin ? "admin-home-root" : undefined}>
      {awaitingPermissions ? null : !isAdmin ? (
        <div className="student-landing">
          <Navbar studentLanding scrolled={navScrolled} />
          {isFirstLogin && (
            <UserDetailsModal
              userEmail={email}
              onDetailsSaved={handleDetailsSaved}
            />
          )}
          {!isFirstLogin && (
            <>
              <MainImageHeader
                loggedIn={isAuthenticated}
                onExplore={enterContent}
              />
              <div className="student-landing__body">
                <AnimateComponent>
                  <MainButtonControls />
                </AnimateComponent>
                <AnimateComponent>
                  <HomeCarousel />
                </AnimateComponent>
                <AnimateComponent>
                  <MainOfferings teachers={teachers} />
                </AnimateComponent>
                <AnimateComponent>
                  <MainAbout />
                </AnimateComponent>
                <AnimateComponent>
                  <MainContact />
                </AnimateComponent>
                <Footer />
              </div>

              {showCheckout && regObj && (
                <PendingFeePopup
                  regObj={regObj}
                  onClose={() => setShowCheckout(false)}
                  onResolved={() => setShowCheckout(false)}
                />
              )}
              {showFinancialAidModal && (
                <FinancialAidModal
                  userEmail={email}
                  id={userId}
                  onDetailsSaved={handleFinancialDetailsSaved}
                  onClose={() => setShowFinancialAidModal(false)}
                />
              )}
            </>
          )}
        </div>
      ) : (
        <div className="admin-dashboard-shell" data-admin-shell="true">
          <div className="admin-dashboard-backdrop" />

          <div className="admin-dashboard-header">
            <div className="admin-brand-logo-slot">
              <img
                src={BRAND.logoSrc}
                alt={BRAND.name}
                className="admin-brand-logo"
              />
            </div>
            <span className="admin-brand-label">Admin Portal</span>
          </div>

          <div className="admin-dashboard-content">
            <AdminDashboard
              adminName={adminName}
              modules={visibleAdminOptions}
              onLogout={() => logout({ returnTo: window.location.origin })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
